import axios from "axios";
import store from "@/store";
import { applyAuthTokensFromResponse, getRefreshTokenConfig, isCookieRefreshEnabled } from "@/utils/auth-tokens";
import { clearRefreshLock, isAnotherTabRefreshing, setRefreshLock, waitForRefreshLockToClear } from "@/utils/tokenRefreshLock";
import { createTokenChannel } from "@/utils/tokenChannel";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
  },
});

let refreshPromise = null;
let tokenChannel = null;

const REFRESH_ERROR_REASON = {
  REFRESH_TOKEN_NOT_RECEIVED: "REFRESH_TOKEN_NOT_RECEIVED",
  REFRESH_TOKEN_INVALID: "REFRESH_TOKEN_INVALID",
  REFRESH_TOKEN_EXPIRED: "REFRESH_TOKEN_EXPIRED",
  REFRESH_TOKEN_USER_NOT_FOUND: "REFRESH_TOKEN_USER_NOT_FOUND",
  REFRESH_TOKEN_USER_INACTIVE: "REFRESH_TOKEN_USER_INACTIVE",
  GOOGLE_REFRESH_TOKEN_MISSING: "GOOGLE_REFRESH_TOKEN_MISSING",
  GOOGLE_ACCESS_TOKEN_MISSING: "GOOGLE_ACCESS_TOKEN_MISSING",
  GOOGLE_BACKEND_TOKEN_UPDATE_FAILED: "GOOGLE_BACKEND_TOKEN_UPDATE_FAILED",
  DJANGO_ACCESS_TOKEN_MISSING: "DJANGO_ACCESS_TOKEN_MISSING",
  UNSUPPORTED_AUTH_USER_TYPE: "UNSUPPORTED_AUTH_USER_TYPE",
};

const BE_REFRESH_ERROR_REASON_BY_CODE = {
  1001053: REFRESH_ERROR_REASON.REFRESH_TOKEN_NOT_RECEIVED,
  1001054: REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID,
  1001061: REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID,
  1001062: REFRESH_ERROR_REASON.REFRESH_TOKEN_EXPIRED,
  1001063: REFRESH_ERROR_REASON.REFRESH_TOKEN_NOT_RECEIVED,
  1001065: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_NOT_FOUND,
  1001066: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_INACTIVE,
  1001079: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_NOT_FOUND,
};

function createRefreshError(reason, message, cause = null) {
  const error = new Error(message);
  error.reason = reason;
  error.cause = cause;
  return error;
}

function getBackendErrorData(error) {
  return error && error.response && error.response.data ? error.response.data : {};
}

function getBackendRefreshErrorReason(error) {
  const errorData = getBackendErrorData(error);
  const errCode = Number(errorData.err_code);
  return BE_REFRESH_ERROR_REASON_BY_CODE[errCode] || null;
}

function createBackendRefreshError(error) {
  const errorData = getBackendErrorData(error);
  const reason = getBackendRefreshErrorReason(error) || REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID;
  const message = errorData.err_msg || errorData.detail || "Failed to refresh access token";
  const refreshError = createRefreshError(reason, message, error);
  refreshError.errCode = errorData.err_code;
  refreshError.errMsg = errorData.err_msg;
  return refreshError;
}

function getTokenChannel() {
  if (!tokenChannel) {
    tokenChannel = createTokenChannel((token) => {
      store.commit("setAccessToken", token);
    });
  }

  return tokenChannel;
}

export function isTokenRefreshRequest(url = "") {
  return /account\/update-token(?:-recolumn)?\/?/.test(url);
}

export function isAuthRequest(url = "") {
  return /account\/(?:login|google-oauth|sign-up[^/]*)\/?/.test(url);
}

export function isLicenseActiveError(error) {
  return Boolean(
    error &&
    error.response &&
    error.response.status === 429 &&
    error.response.data &&
    String(error.response.data.err_code) === "1001175"
  );
}

export function isGoogleLoginEnabled() {
  return process.env.VUE_APP_ENABLE_GOOGLE_LOGIN !== "false";
}

function getUserType() {
  return store.state.user_info && store.state.user_info.user_type;
}

function commitTokens(data = {}) {
  applyAuthTokensFromResponse(store, data);
  if (data.refresh_token || data.refreshToken) {
    store.commit("setRefreshToken", data.refresh_token || data.refreshToken);
  }
}

async function refreshGoogleToken() {
  if (!store.state.refresh_token) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_REFRESH_TOKEN_MISSING,
      "Google refresh token is missing"
    );
  }

  const googleTokenRes = await axios.post("https://oauth2.googleapis.com/token", {
    client_id: process.env.VUE_APP_CLIENT_ID,
    client_secret: process.env.VUE_APP_CLIENT_SECRET,
    refresh_token: store.state.refresh_token,
    grant_type: "refresh_token",
  });

  const newAccessToken = googleTokenRes.data.access_token;
  if (!newAccessToken) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_ACCESS_TOKEN_MISSING,
      "Google access token missing from refresh response"
    );
  }

  store.commit("setAccessToken", newAccessToken);
  if (googleTokenRes.data.id_token) {
    store.commit("setIdToken", googleTokenRes.data.id_token);
  }
  getTokenChannel().postMessage({ type: "NEW_ACCESS_TOKEN", token: newAccessToken });

  try {
    await apiClient.get("account/update-token/", {
      headers: { Authorization: `Bearer ${newAccessToken}` },
    });
  } catch (error) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_BACKEND_TOKEN_UPDATE_FAILED,
      "Failed to sync refreshed Google access token with backend",
      error
    );
  }

  return newAccessToken;
}

async function refreshDjangoToken() {
  const refreshConfig = getRefreshTokenConfig(store.state.refresh_token, store.state.auth_refresh_mode);
  let djangoTokenRes = null;

  try {
    djangoTokenRes = await apiClient.get("account/update-token-recolumn/", refreshConfig);
  } catch (error) {
    throw createBackendRefreshError(error);
  }

  commitTokens(djangoTokenRes.data);

  const newAccessToken = djangoTokenRes.data.access_token || djangoTokenRes.data.accessToken;
  if (!newAccessToken) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.DJANGO_ACCESS_TOKEN_MISSING,
      "Django access token missing from refresh response"
    );
  }

  getTokenChannel().postMessage({ type: "NEW_ACCESS_TOKEN", token: newAccessToken });
  return newAccessToken;
}

async function runRefresh() {
  store.commit("setIsMethodRecalling", true);
  setRefreshLock();

  try {
    const userType = getUserType();

    if (userType === "google") {
      return await refreshGoogleToken();
    }

    if (userType === "django" || userType === "django-prosecution") {
      return await refreshDjangoToken();
    }

    if (isCookieRefreshEnabled(store.state.auth_refresh_mode) && Object.keys(store.state.user_info || {}).length > 0) {
      return await refreshDjangoToken();
    }

    throw createRefreshError(
      REFRESH_ERROR_REASON.UNSUPPORTED_AUTH_USER_TYPE,
      "Unsupported auth user type"
    );
  } finally {
    clearRefreshLock();
    store.commit("setIsMethodRecalling", false);
  }
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  if (isAnotherTabRefreshing()) {
    await waitForRefreshLockToClear();
    if (store.state.access_token) return store.state.access_token;
  }

  refreshPromise = runRefresh()
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export function refreshAccessTokenAsMethodRecall() {
  return refreshAccessToken()
    .then(() => "access token 갱신 및 저장 완료 -> method 재호출")
    .catch(() => Promise.reject(1));
}

export function handleRefreshFailure() {
  store.commit("setOverlay", false);
  store.commit("setOverlayText", "");
  store.commit("logout");
  import("@/router").then(({ default: router }) => {
    router.push("/").catch(() => {});
  });
}
